import type { LevelNumber } from './progress'

/**
 * Mastery gating, the ceiling, and the scaffold — PROGRESSION_MODEL_3_LEVELS.
 *
 * ─── §1 · the rule ──────────────────────────────────────────────────────────
 *
 *     MASTERED when  roundsAtLevel   >= 3      // exposure floor — 9 scenes
 *              AND   firstTryCorrect >= 5 of the last 6      (~83%)
 *
 * Evaluated at ROUND END, applied at the START of the next round of that game.
 *
 * 🔴 THE EXPOSURE FLOOR IS NOT NEGOTIABLE. With 2 cards at L1 a child guessing
 * randomly gets ~50%, so a lucky run of 5 in 6 is genuinely possible. Three
 * rounds minimum plus the 5-of-6 window makes chance advancement about a 1-in-9
 * event, and the ceiling below catches it when it happens anyway.
 *
 * ─── §2 · the three things that must never count ────────────────────────────
 *
 * **Gesture success.** Gating content on motor performance would make a smaller
 * or less coordinated child — or one on a weak laptop — fail an academic level
 * for a physical reason. It would also make the tap path second-class, which
 * breaks the single-code-path property that keeps it working. Mastery reads the
 * POINTING CHOICE and nothing else.
 *
 * **Speed.** Reaction time at 3–6 measures temperament, not learning.
 *
 * **Retries.** The child gets unlimited attempts; only the FIRST tells you what
 * they knew. That is the entire reason the ring buffer stores first-try flags
 * rather than final outcomes.
 *
 * ─── §3 · why there is a ceiling ────────────────────────────────────────────
 *
 * Mastery gating without a ceiling reintroduces the fail state this product does
 * not have: a child stuck repeating L1 while the class moves on. So support
 * ramps at rounds 4–5, and at round 6 the level advances regardless, scaffolded.
 * The worst case is "reached L3 slowly, with more help" — never "did not pass".
 *
 * 🔴 NO LOCK IS EVER RENDERED. Nothing in this file produces a "locked" state, a
 * padlock, or a not-yet screen. The child sees one path forward.
 */

/** How many rounds before mastery may fire at all. */
export const EXPOSURE_FLOOR_ROUNDS = 3
/** The window mastery is judged over. */
export const MASTERY_WINDOW = 6
/** How many of that window must be right first time. */
export const MASTERY_REQUIRED = 5
/** The round at which the level advances whether or not mastery fired. */
export const CEILING_ROUNDS = 6
/** Rounds after a ceiling advance that run with extra support. */
export const SCAFFOLD_ROUNDS = 2

/**
 * Per-game progression state — PROGRESSION §5.2.
 *
 * 🔴 A SNAPSHOT, NOT A COUNTER SET. Progress is upserted whole rather than
 * incremented, which is what makes it replay-safe — and it has to be, because an
 * event can arrive hours late from the offline outbox.
 */
export interface GameProgress {
  level: LevelNumber
  roundsAtLevel: number
  /**
   * First-try correctness for the last six scenes, oldest first.
   *
   * 🔴 CONTENT ONLY. A 1 here means the child pointed at the right card on their
   * first attempt. Gestures never touch this array — see §2.
   */
  lastSixFirstTry: number[]
  masteredLevels: LevelNumber[]
  scaffoldRoundsRemaining: number
}

export const initialGameProgress = (): GameProgress => ({
  level: 1,
  roundsAtLevel: 0,
  lastSixFirstTry: [],
  masteredLevels: [],
  scaffoldRoundsRemaining: 0,
})

/** Appends a scene's first-try result, keeping only the last six. */
export function recordScene(state: GameProgress, firstTryCorrect: boolean): GameProgress {
  const next = [...state.lastSixFirstTry, firstTryCorrect ? 1 : 0]
  return { ...state, lastSixFirstTry: next.slice(-MASTERY_WINDOW) }
}

/** §1's rule, as a predicate. Pure, so the playtest can replay it over real data. */
export function hasMastered(state: GameProgress): boolean {
  if (state.roundsAtLevel < EXPOSURE_FLOOR_ROUNDS) return false
  // 🔴 The window must be FULL. Five of five is not five of six: a child three
  // scenes in has not yet been asked six questions, and advancing them there
  // would be exactly the chance advancement the exposure floor exists to stop.
  if (state.lastSixFirstTry.length < MASTERY_WINDOW) return false
  const correct = state.lastSixFirstTry.reduce((a, b) => a + b, 0)
  return correct >= MASTERY_REQUIRED
}

/** Why a level advanced. Drives the `LEVEL_UP` label, and a metric that matters. */
export type AdvanceReason = 'mastered' | 'ceiling'

export interface RoundEnd {
  state: GameProgress
  /** Set only on the round the level actually changes. */
  advanced: { to: LevelNumber; reason: AdvanceReason } | null
}

/**
 * Ends a round: bump the counter, then decide whether the level moves.
 *
 * 🔴 IDEMPOTENT ON `masteredLevels` (§5.5). A replayed round from the offline
 * outbox must not fire the celebration twice — a child getting "you're ready for
 * something new!" three times when the queue flushes reads as a broken product,
 * and it is the kind of bug that only ever appears on a bad network.
 */
export function endRound(state: GameProgress): RoundEnd {
  const roundsAtLevel = state.roundsAtLevel + 1
  const bumped: GameProgress = {
    ...state,
    roundsAtLevel,
    scaffoldRoundsRemaining: Math.max(0, state.scaffoldRoundsRemaining - 1),
  }

  const top = bumped.level >= 3
  const mastered = hasMastered(bumped)
  const atCeiling = roundsAtLevel >= CEILING_ROUNDS

  if (!mastered && !atCeiling) return { state: bumped, advanced: null }

  /*
   * 🔴 THE TOP LEVEL STILL RECORDS MASTERY, IT JUST HAS NOWHERE TO GO. Without
   * this, a child who masters L3 would be re-evaluated every round forever and
   * the "game complete" moment would never be reachable.
   */
  if (top) {
    if (!mastered || bumped.masteredLevels.includes(bumped.level)) {
      return { state: bumped, advanced: null }
    }
    return {
      state: { ...bumped, masteredLevels: [...bumped.masteredLevels, bumped.level] },
      advanced: null,
    }
  }

  const to = (bumped.level + 1) as LevelNumber
  const reason: AdvanceReason = mastered ? 'mastered' : 'ceiling'

  return {
    state: {
      ...bumped,
      level: to,
      roundsAtLevel: 0,
      // The window is per-level: carrying it forward would judge a child's first
      // L2 round against the L1 questions they were answering last week.
      lastSixFirstTry: [],
      masteredLevels: mastered
        ? [...new Set([...bumped.masteredLevels, bumped.level])]
        : bumped.masteredLevels,
      /*
       * 🔴 A CEILING ADVANCE ARRIVES WITH SUPPORT, NOT WITHOUT IT. The next
       * level runs its first two rounds at reduced choice count with more review
       * scenes — otherwise advancing a struggling child is just moving them
       * somewhere harder.
       */
      scaffoldRoundsRemaining: reason === 'ceiling' ? SCAFFOLD_ROUNDS : 0,
    },
    advanced: { to, reason },
  }
}

/**
 * How a round should be shaped for this child, right now — PROGRESSION §3.
 *
 * 🔴 THE CHILD IS NEVER TOLD THE DIFFICULTY CHANGED. This returns a shape, not a
 * message. Nothing in the UI may announce that the cards got easier: a
 * five-year-old who works that out has been told they are behind.
 *
 * ⚠️ NOTHING CALLS THIS YET — CONFORMANCE C10, found in HH-4.
 *
 * Every round in the product is `sampleRound(levelData, pick)`, which is handed
 * ONE level and returns whole items. It cannot reach the level below for a
 * review scene, and it cannot drop a choice — the stage renders `item.choices`
 * verbatim. So a child moved up by the ceiling arrives at the harder level with
 * none of the support this function computes for them, which is precisely the
 * outcome §3 above exists to prevent.
 *
 * The gap is invisible from here because this function is correct and its unit
 * tests pass. Do not "fix" it by changing anything in this file: the missing
 * piece is a round assembler that consults it.
 */
export interface RoundShape {
  /** How many cards to show. Never below two — one card is not a choice. */
  choiceCount: number
  /** How many of this round's scenes come from the previous level's pool. */
  reviewScenes: number
  /** True while the child is being supported after a ceiling advance. */
  scaffolded: boolean
}

/** The choice count each level normally asks for — PROGRESSION §1. */
export const CHOICES_BY_LEVEL: Record<LevelNumber, number> = { 1: 2, 2: 3, 3: 4 }

export function roundShape(state: GameProgress): RoundShape {
  const base = CHOICES_BY_LEVEL[state.level]
  const scaffolded = state.scaffoldRoundsRemaining > 0

  /*
   * Rounds 4–5 at a level: items EASE. Choice count drops one step and the
   * distractors get more obviously wrong. Content is unchanged — this is not a
   * different lesson, it is the same lesson with more room.
   */
  const easing = state.roundsAtLevel >= EXPOSURE_FLOOR_ROUNDS + 1
  const drop = scaffolded || easing ? 1 : 0

  return {
    choiceCount: Math.max(2, base - drop),
    // One review scene in three normally; two while scaffolded, so a supported
    // round still guarantees a win the child can actually reach.
    reviewScenes: state.level === 1 ? 0 : scaffolded ? 2 : 1,
    scaffolded,
  }
}
